import { useSelector } from "react-redux";
import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import AdminShell from "./AdminShell";
import { dialog } from "../Common/TreasuryDialog";

const API = process.env.REACT_APP_API_URL || "";

const emptyEval = { name: "", value: "", date: "" };

const PropertyEvaluations = () => {
  const { propertyId } = useParams();
  const [property, setProperty] = useState(null);
  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    period: "",
    evaluator1: { ...emptyEval },
    evaluator2: { ...emptyEval },
  });

  const { auth } = useSelector((state) => state);
  const token = auth?.data?.accesstoken || auth?.data?.access_token;
  const authHeader = { Authorization: `Bearer ${token}` };

  const loadData = useCallback(() => {
    setLoading(true);
    Promise.all([
      fetch(`${API}/api/property/getById/${propertyId}`, { headers: authHeader }).then((r) => r.json()),
      fetch(`${API}/api/admin/evaluations/${propertyId}`, { headers: authHeader }).then((r) => r.json()),
    ])
      .then(([propData, evalData]) => {
        setProperty(propData.property || propData);
        setEvaluations(evalData.evaluations || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [propertyId, token]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.period) {
      dialog.error("Validation", "Period is required (e.g. H1-2026).");
      return;
    }
    try {
      const body = {
        propertyId,
        period: form.period,
        evaluator1: {
          name: form.evaluator1.name,
          value: parseFloat(form.evaluator1.value) || null,
          date: form.evaluator1.date || null,
        },
        evaluator2: {
          name: form.evaluator2.name,
          value: parseFloat(form.evaluator2.value) || null,
          date: form.evaluator2.date || null,
        },
      };
      const res = await fetch(`${API}/api/admin/evaluations`, {
        method: "POST",
        headers: { ...authHeader, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.status === 1) {
        dialog.success("Success", data.msg);
        setShowForm(false);
        setForm({ period: "", evaluator1: { ...emptyEval }, evaluator2: { ...emptyEval } });
        loadData();
      } else {
        dialog.error("Error", data.msg);
      }
    } catch {
      dialog.error("Error", "Network error.");
    }
  };

  const handleDelete = async (id) => {
    const confirmed = await dialog.confirm("Delete Evaluation", "Are you sure you want to delete this evaluation?");
    if (!confirmed) return;
    const res = await fetch(`${API}/api/admin/evaluations/${id}`, {
      method: "DELETE",
      headers: authHeader,
    });
    const data = await res.json();
    if (data.status === 1) {
      loadData();
    } else {
      dialog.error("Error", data.msg);
    }
  };

  const setEval = (n, field, val) =>
    setForm((f) => ({ ...f, [`evaluator${n}`]: { ...f[`evaluator${n}`], [field]: val } }));

  return (
    <AdminShell pageTitle={`Evaluations — ${property?.title || "Property"}`}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#111827" }}>
            {property?.title}
          </h2>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: "#6b7280" }}>
            {property?.city} · Bi-annual property evaluations
          </p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          style={{
            padding: "10px 20px",
            background: "linear-gradient(135deg,#c9a84c,#7a5c10)",
            color: "#fff",
            border: "none",
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          {showForm ? "Cancel" : "+ Add Evaluation"}
        </button>
      </div>

      {/* Add form */}
      {showForm && (
        <div style={{ background: "#fff", borderRadius: 12, padding: 28, boxShadow: "0 1px 8px rgba(0,0,0,0.07)", marginBottom: 24 }}>
          <h3 style={{ margin: "0 0 20px", fontSize: 15, fontWeight: 700, color: "#111827" }}>New Evaluation</h3>
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 18 }}>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 }}>
                Period * (e.g. H1-2026, H2-2026)
              </label>
              <input
                value={form.period}
                onChange={(e) => setForm((f) => ({ ...f, period: e.target.value }))}
                placeholder="H1-2026"
                style={{ width: "100%", padding: "10px 12px", border: "1.5px solid #e5e7eb", borderRadius: 8, fontSize: 14, boxSizing: "border-box" }}
              />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
              {[1, 2].map((n) => (
                <div key={n}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: "#374151", marginBottom: 12 }}>
                    Evaluator {n}
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#6b7280", marginBottom: 4 }}>Name</label>
                    <input
                      value={form[`evaluator${n}`].name}
                      onChange={(e) => setEval(n, "name", e.target.value)}
                      placeholder="Licensed evaluator name"
                      style={{ width: "100%", padding: "9px 12px", border: "1.5px solid #e5e7eb", borderRadius: 8, fontSize: 13, boxSizing: "border-box" }}
                    />
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#6b7280", marginBottom: 4 }}>Valuation (SAR)</label>
                    <input
                      type="number"
                      value={form[`evaluator${n}`].value}
                      onChange={(e) => setEval(n, "value", e.target.value)}
                      placeholder="e.g. 2500000"
                      style={{ width: "100%", padding: "9px 12px", border: "1.5px solid #e5e7eb", borderRadius: 8, fontSize: 13, boxSizing: "border-box" }}
                    />
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#6b7280", marginBottom: 4 }}>Date</label>
                    <input
                      type="date"
                      value={form[`evaluator${n}`].date}
                      onChange={(e) => setEval(n, "date", e.target.value)}
                      style={{ width: "100%", padding: "9px 12px", border: "1.5px solid #e5e7eb", borderRadius: 8, fontSize: 13, boxSizing: "border-box" }}
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Average preview */}
            {form.evaluator1.value && form.evaluator2.value && (
              <div style={{ background: "#fefce8", border: "1px solid #fde68a", borderRadius: 8, padding: "12px 16px", marginTop: 20, fontSize: 13 }}>
                <strong style={{ color: "#92400e" }}>Average Valuation:</strong>{" "}
                <span style={{ color: "#78350f", fontWeight: 700 }}>
                  SAR {((parseFloat(form.evaluator1.value) + parseFloat(form.evaluator2.value)) / 2).toLocaleString()}
                </span>
              </div>
            )}

            <button
              type="submit"
              style={{
                marginTop: 20,
                padding: "11px 28px",
                background: "linear-gradient(135deg,#c9a84c,#7a5c10)",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              Save Evaluation
            </button>
          </form>
        </div>
      )}

      {/* Evaluations list */}
      <div style={{ background: "#fff", borderRadius: 12, padding: 28, boxShadow: "0 1px 8px rgba(0,0,0,0.07)" }}>
        <h3 style={{ margin: "0 0 20px", fontSize: 15, fontWeight: 700, color: "#111827" }}>
          Evaluation History
        </h3>
        {loading ? (
          <div style={{ textAlign: "center", color: "#9ca3af", padding: "40px 0" }}>Loading…</div>
        ) : evaluations.length === 0 ? (
          <div style={{ textAlign: "center", color: "#9ca3af", padding: "40px 0", fontSize: 14 }}>
            No evaluations recorded yet
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {evaluations.map((ev) => (
              <div
                key={ev._id}
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 10,
                  padding: "18px 20px",
                  display: "grid",
                  gridTemplateColumns: "120px 1fr 1fr 1fr 80px",
                  gap: 16,
                  alignItems: "center",
                }}
              >
                <div>
                  <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 2 }}>Period</div>
                  <div style={{ fontWeight: 700, color: "#c9a84c", fontSize: 15 }}>{ev.period}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 2 }}>Evaluator 1</div>
                  <div style={{ fontWeight: 600, color: "#111827" }}>{ev.evaluator1?.name || "—"}</div>
                  <div style={{ fontSize: 12, color: "#374151" }}>
                    {ev.evaluator1?.value ? `SAR ${ev.evaluator1.value.toLocaleString()}` : "—"}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 2 }}>Evaluator 2</div>
                  <div style={{ fontWeight: 600, color: "#111827" }}>{ev.evaluator2?.name || "—"}</div>
                  <div style={{ fontSize: 12, color: "#374151" }}>
                    {ev.evaluator2?.value ? `SAR ${ev.evaluator2.value.toLocaleString()}` : "—"}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 2 }}>Average Value</div>
                  <div style={{ fontWeight: 700, color: "#166534", fontSize: 15 }}>
                    {ev.averageValue ? `SAR ${ev.averageValue.toLocaleString()}` : "—"}
                  </div>
                </div>
                <button
                  onClick={() => handleDelete(ev._id)}
                  style={{
                    padding: "6px 12px",
                    background: "#fee2e2",
                    color: "#dc2626",
                    border: "none",
                    borderRadius: 6,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Delete
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminShell>
  );
};

export default PropertyEvaluations;
